/**
 * Review comment context + provider.
 *
 * Single source of truth for the reviewer-comments UI on a draft preview page.
 * The provider loads the full comment list ONCE per (pageType, slug) and
 * exposes actions that mutate the list optimistically before re-fetching on
 * success. Filter state persists to localStorage so a reviewer's preference
 * survives page refreshes.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const FILTER_KEY = "vv.reviewer.commentFilter";
const OPEN_PANEL_KEY = "vv.reviewer.panelOpen";

const ReviewContext = createContext(null);

export function useReview() {
  const ctx = useContext(ReviewContext);
  if (!ctx) throw new Error("useReview must be used within <ReviewProvider>");
  return ctx;
}

export function ReviewProvider({ pageType, slug, user, children }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    if (typeof window === "undefined") return "all";
    return window.localStorage.getItem(FILTER_KEY) || "all";
  });
  const [panelOpen, setPanelOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem(OPEN_PANEL_KEY) !== "false";
  });
  // Selection state for the "add comment on selection" flow — populated by
  // ReviewLayer, read by CommentComposer.
  const [pendingAnchor, setPendingAnchor] = useState(null);
  // { anchor: string, excerpt: string, label: string } | null
  const activeHighlightId = useRef(null);

  const load = useCallback(async () => {
    if (!pageType || !slug) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/comments/${pageType}/${slug}`);
      setComments(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [pageType, slug]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(FILTER_KEY, filter);
  }, [filter]);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(OPEN_PANEL_KEY, String(panelOpen));
  }, [panelOpen]);

  const addComment = useCallback(async ({ anchor, excerpt, text, parent_id }) => {
    const payload = {
      page_type: pageType,
      slug,
      anchor: anchor || "field:general",
      excerpt: excerpt || "",
      text,
      parent_id: parent_id || null,
    };
    const { data } = await api.post("/admin/comments", payload);
    setComments((prev) => [...prev, data]);
    setPendingAnchor(null);
    toast.success(parent_id ? "Reply added" : "Comment added");
    return data;
  }, [pageType, slug]);

  const setStatus = useCallback(async (cid, status) => {
    // Optimistic update first for a snappy Resolve/Reopen click.
    setComments((prev) => prev.map((c) => c.id === cid ? { ...c, status } : c));
    try {
      await api.post(`/admin/comments/${cid}/status`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
    } catch (e) {
      // Rollback on failure
      toast.error(e.response?.data?.detail || "Failed to update status");
      await load();
    }
  }, [load]);

  // Group by anchor + build root/reply threads.
  const threads = useMemo(() => {
    const roots = comments.filter((c) => !c.parent_id);
    return roots.map((root) => ({
      root,
      replies: comments
        .filter((c) => c.parent_id === root.id)
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")),
    }));
  }, [comments]);

  const filteredThreads = useMemo(() => {
    if (filter === "all") return threads;
    return threads.filter((t) => t.root.status === (filter === "open" ? "open" : "resolved"));
  }, [threads, filter]);

  const counts = useMemo(() => {
    const c = { all: threads.length, open: 0, resolved: 0 };
    threads.forEach((t) => {
      if (t.root.status === "resolved") c.resolved += 1;
      else c.open += 1;
    });
    return c;
  }, [threads]);

  const value = useMemo(() => ({
    pageType, slug, user, loading, comments, threads: filteredThreads, counts,
    filter, setFilter, panelOpen, setPanelOpen,
    pendingAnchor, setPendingAnchor,
    activeHighlightId,
    addComment, setStatus, refresh: load,
  }), [pageType, slug, user, loading, comments, filteredThreads, counts,
      filter, panelOpen, pendingAnchor, addComment, setStatus, load]);

  return <ReviewContext.Provider value={value}>{children}</ReviewContext.Provider>;
}
